import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/formularios.css';
import '../styles/styles.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png.jpg';
import { resolveApiBaseList } from '../utils/apiBase';

const ConsultarEvidencias = () => {
  const [panelActivo, setPanelActivo] = useState(null);
  const [imagenActual, setImagenActual] = useState(null);
  const [visorVisible, setVisorVisible] = useState(false);

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [evidencias, setEvidencias] = useState([]); // [{id, docente, descripcion, tema, idPlaneaciones, archivos:[{nombre,src}]}]
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const togglePanel = (id) => setPanelActivo(panelActivo === id ? null : id);
  const abrirImagenVista = (src) => { setImagenActual(src); setVisorVisible(true); };
  const cerrarImagen = () => { setImagenActual(null); setVisorVisible(false); };
  const isImage = (url = '') => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url);

  // Agrupa filas planas en objetos por IdEvidencia y ordena: nuevo -> viejo
  const agrupar = useCallback((rows = [], baseUrl = '') => {
    const normalizedBase = baseUrl.replace(/\/$/, '');
    const map = new Map();
    for (const r of rows) {
      const id = r.IdEvidencia;
      if (!map.has(id)) {
        map.set(id, {
          id,
          docente: r.DocenteNombre || '—',
          descripcion: r.Descripcion || '',
          tema: r.Tema || `Planeación #${r.IdPlaneaciones}`,
          idPlaneaciones: r.IdPlaneaciones,
          archivos: [],
        });
      }
      const item = map.get(id);
      const rel = String(r.ResultadosURL || '');
      const cleanPath = rel.replace(/^\/+/, '');
      const src = rel.startsWith('http')
        ? rel
        : normalizedBase
          ? `${normalizedBase}/${cleanPath}`
          : `/${cleanPath}`;
      const nombre = rel.split('/').pop() || 'archivo';
      item.archivos.push({ nombre, src });
    }

    // Ordena archivos (si llevan timestamp en el nombre quedarán nuevo->viejo)
    for (const ev of map.values()) {
      ev.archivos.sort((a, b) => b.src.localeCompare(a.src));
    }

    // Evidencias: id mayor primero (más nuevo)
    return Array.from(map.values()).sort((a, b) => b.id - a.id);
  }, []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // El backend respondió texto/HTML, se mantiene message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          const payload = trimmed ? JSON.parse(trimmed) : {};
          return { data: payload, baseUrl: normalizedBase };
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      setError('');
      try {
        // Espera que el backend devuelva filas como:
        // { IdEvidencia, Descripcion, ResultadosURL, IdPlaneaciones, Tema, DocenteNombre }
        const { data, baseUrl } = await fetchJsonWithFallback('/evidencias/todas');
        const rows = Array.isArray(data) ? data : (data?.data || []);
        setEvidencias(agrupar(rows, baseUrl));
      } catch (e) {
        console.error(e);
        setError(e?.message || 'Error al consultar evidencias.');
      } finally {
        setCargando(false);
      }
    };
    cargar();

    const onKey = (e) => e.key === 'Escape' && cerrarImagen();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fetchJsonWithFallback, agrupar]);

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Consultar Evidencias </small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {cargando && <div className="alert alert-info">Cargando evidencias…</div>}
      {!!error && <div className="alert alert-danger">{error}</div>}
      {!cargando && !error && evidencias.length === 0 && (
        <div className="alert alert-warning">No hay evidencias registradas.</div>
      )}

      {/* Tarjetas */}
      <div className="row g-4">
        {evidencias.map((ev) => (
          <div className="col-md-6" key={ev.id}>
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <h5 className="card-title mb-1">Evidencia #{ev.id}</h5>
                  <span className="badge text-bg-secondary">{ev.docente}</span>
                </div>
                <div className="text-muted small mb-2">
                  {ev.tema} {ev.idPlaneaciones ? `(Planeación #${ev.idPlaneaciones})` : ''}
                </div>
                <p className="card-text">
                  <strong>Descripción:</strong> {ev.descripcion || '—'}
                </p>

                <button
                  className="btn btn-outline-primary btn-sm mt-1"
                  onClick={() => togglePanel(ev.id)}
                >
                  {panelActivo === ev.id ? 'Ocultar archivos' : 'Mostrar archivos'}
                </button>

                {panelActivo === ev.id && (
                  <div className="mt-3">
                    <label className="form-label">Archivos</label>
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle text-center">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Vista/Link</th>
                            <th>Nombre</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ev.archivos.map((a, idx) => (
                            <tr key={`${ev.id}-${idx}`}>
                              <td>{idx + 1}</td>
                              <td style={{ minWidth: 120 }}>
                                {isImage(a.src) ? (
                                  <img
                                    src={a.src}
                                    alt={a.nombre}
                                    className="preview-img"
                                    onClick={() => abrirImagenVista(a.src)}
                                    style={{ cursor: 'pointer', maxWidth: '80px', maxHeight: '80px' }}
                                  />
                                ) : (
                                  <a className="btn btn-sm btn-outline-secondary" href={a.src} target="_blank" rel="noreferrer">
                                    Abrir archivo
                                  </a>
                                )}
                              </td>
                              <td className="text-break">{a.nombre}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Visor de imagen */}
      {visorVisible && (
        <div
          style={{
            display: 'flex',
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '80vw',
              maxWidth: '900px',
              height: '80vh',
              background: 'white',
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 0 30px rgba(0,0,0,0.4)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ background: '#343a40', color: 'white', padding: '10px 20px', fontWeight: 'bold' }}>
              Vista previa de la imagen
              <button onClick={cerrarImagen} className="btn btn-danger btn-sm" style={{ float: 'right' }}>✖</button>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
              <img src={imagenActual || ''} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultarEvidencias;
